import { shortcutLocalise } from "@web/src/app/utils/shortcuts";
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import classNames from "classnames";
import type { FC } from "react";
import React from "react";
import type { IconType } from "react-icons";

type HotkeyProps = VariantProps<typeof hotkeyClass> & {
  combo?: string;
  verbose?: boolean;
  merge?: boolean;
  /// defaults to true
  uppercase?: boolean;
};

const hotkeyClass = cva("rounded-[2px] leading-4 font-inter font-medium text-center", {
  variants: {
    mode: {
      contrast: "",
      default: "",
    },
    size: {
      small: "px-[4px] py-[0px] text-2xs min-w-[16px]",
      medium: "px-[6px] py-[2px] text-xs min-w-[18px]",
      large: "px-[8px] py-[3px] text-sm min-w-[20px]",
    },
    color: {
      primary: "",
      secondary: "",
      tertiary: "",
    },
  },
  compoundVariants: [
    {
      mode: "default",
      color: "primary",
      class: "bg-surface-prm text-glyph-prm group-hover:bg-surface-scd",
    },
    {
      mode: "default",
      color: "secondary",
      class: `bg-surface-scd text-glyph-prm group-hover:bg-surface-trt`,
    },
    {
      mode: "default",
      color: "tertiary",
      class: `bg-surface-scd-overlay text-glyph-prm group-hover:bg-surface-scd`,
    },
    {
      mode: "contrast",
      color: "primary",
      class: "bg-surface-cst text-glyph-cst-prm group-hover:bg-surface-cst-scd",
    },
    {
      mode: "contrast",
      color: "secondary",
      class: "bg-surface-cst-scd text-glyph-cst-prm group-hover:bg-surface-cst-trt",
    },
    {
      mode: "contrast",
      color: "tertiary",
      class: "bg-surface-cst-scd-overlay text-glyph-cst-prm group-hover:bg-surface-cst-scd",
    },
  ],
  defaultVariants: {
    mode: "default",
    color: "primary",
    size: "medium",
  },
});

const iconForHotkey: Record<string, IconType> = {};

export const Hotkey: FC<HotkeyProps> = ({
  combo,
  verbose = false,
  merge = false,
  size,
  mode,
  color,
  uppercase = true,
}) => {
  const hotkeys = combo?.split("+").map((k) => k.trim()) ?? [];

  return (
    <div
      className={classNames("inline-flex flex-row items-center justify-center whitespace-nowrap", {
        uppercase,
        "gap-1": size !== "small",
        "gap-[0.125rem]": size === "small",
      })}
    >
      {merge && (
        <div className={hotkeyClass({ mode, color, size })}>
          {hotkeys.map((key, i) => {
            const Icon = iconForHotkey[key];
            return (
              <React.Fragment key={key}>
                {Icon ? <Icon /> : shortcutLocalise(key, verbose)}
                {i !== hotkeys.length - 1 && " + "}
              </React.Fragment>
            );
          })}
        </div>
      )}
      {!merge &&
        hotkeys.map((key) => {
          const Icon = iconForHotkey[key];
          return (
            <div key={key} className={hotkeyClass({ mode, color, size })}>
              {Icon ? <Icon /> : shortcutLocalise(key, verbose)}
            </div>
          );
        })}
    </div>
  );
};
